import { type Configuration, LogLevel } from '@azure/msal-browser';

/**
 * Extract tenantId from URL path.
 * Supports pattern: /tenant/{guid}/...
 */
export const getTenantIdFromUrl = (): string | undefined => {
  const path = window.location.pathname;
  const match = path.match(/^\/tenant\/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})(\/|$)/i);
  return match?.[1];
};

/**
 * Resolve the tenant ID for the initial MSAL authority.
 * Precedence: URL /tenant/{guid} > VITE_AZURE_TENANT_ID env > 'organizations' (multi-tenant default).
 * Uses || (not ??) so empty strings are treated as unset.
 */
export const resolveTenantId = (): string =>
  getTenantIdFromUrl() || import.meta.env.VITE_AZURE_TENANT_ID || 'organizations';

const clientId = import.meta.env.VITE_AZURE_CLIENT_ID ?? '';
const tenantId = resolveTenantId();

// Known production origins for MSAL redirect URIs (registered in Entra).
// Used for validation logging — not a hard block, since preview/AFD deployments
// may use dynamic hostnames (e.g., *.azurefd.net).
const KNOWN_REDIRECT_ORIGINS: readonly string[] = [
  'https://auto.microsoftazurelogicapps.net',
  'https://otto.azure.com',
  'https://otto-canary.azure.com',
  'https://auto.azure.com',
];

/**
 * Resolve the MSAL redirect URI from the current origin.
 * Uses window.location.origin so the redirect matches whichever domain the user accessed.
 * VITE_REDIRECT_URI can override for special cases.
 */
export function resolveRedirectUri(): string {
  // Explicit override takes priority (local dev, special cases)
  if (import.meta.env.VITE_REDIRECT_URI) {
    return import.meta.env.VITE_REDIRECT_URI;
  }

  const origin = window.location.origin;
  const hostname = window.location.hostname;

  // Local dev is always allowed — mock auth or hosts-file redirect to auto.microsoftazurelogicapps.net
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1') {
    return origin + '/login/redirect.html';
  }

  // Warn (not throw) for unknown origins — preview deployments use dynamic AFD hostnames
  // that can't be enumerated here. Entra will reject unregistered URIs at login time
  // with a clear "redirect_uri mismatch" error, so a hard throw adds no safety value.
  if (!KNOWN_REDIRECT_ORIGINS.includes(origin)) {
    console.warn(
      `[Auth] Origin "${origin}" is not in KNOWN_REDIRECT_ORIGINS. ` +
      `Ensure this origin's redirect URI is registered in the Entra app registration. ` +
      `Known: ${KNOWN_REDIRECT_ORIGINS.join(', ')}`
    );
  }

  return origin + '/login/redirect.html';
}

const redirectUri = resolveRedirectUri();

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri,
    postLogoutRedirectUri: redirectUri,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            break;
          case LogLevel.Warning:
            console.warn(message);
            break;
          case LogLevel.Info:
            console.info(message);
            break;
          case LogLevel.Verbose:
            console.debug(message);
            break;
        }
      },
      logLevel: LogLevel.Warning,
    },
  },
};

// Scopes for initial login - OIDC scopes only to avoid preauthorization issues
// Graph scopes removed to prevent AADSTS65002 errors for first-party apps without preauthorization
// Use grantGraphAccess() for incremental consent when Graph features are needed
export const loginRequest = {
  scopes: ['https://graph.microsoft.com/User.ReadBasic.All', 'https://graph.microsoft.com/Group.ReadBasic.All', 'https://graph.microsoft.com/Application.Read.All', 'openid', 'profile', 'offline_access'],
};

// Scopes for Microsoft Graph API calls (requires preauthorization for first-party apps)
// Will fail for first-party apps without preauthorization - Graph features will be disabled
export const graphScopes = {
  scopes: ['https://graph.microsoft.com/User.ReadBasic.All', 'https://graph.microsoft.com/Group.ReadBasic.All', 'https://graph.microsoft.com/Application.Read.All'],
};

// Scopes for Azure Resource Manager API calls
export const armScopes = {
  scopes: ['https://management.azure.com/user_impersonation'],
};

// For third-party apps, use GUID-based identifier
// For first-party apps, use api:// URI format instead (api://${clientId}/.default)
export const dataPlaneScopes = {
  scopes: [`api://${clientId}/.default`],
};

// Scopes for Azure AI Foundry API calls (Agents, Deployments, etc.)
export const cognitiveServicesScopes = {
  scopes: ['https://ai.azure.com/user_impersonation'],
};

// Scopes for Azure Blob Storage access (required for master key retrieval in production)
export const storageScopes = {
  scopes: ['https://storage.azure.com/user_impersonation'],
};
